import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { UX2 } from '@wsb/guac-widget-core';
import { Translate as translate } from '@wsb/vnext-widget-content-translate';
import useCart from '../hooks/useCart';
import BookingFormStore from '../stores/BookingForm';
import ServiceListStore from '../stores/ServiceList';
import AvailableTimeSelectionStore from '../stores/AvailableTimeSelection';
import {
  fetchActivePaymentMethods,
  saveUnpaidBooking,
  updateClient,
  updateLocation,
  updateMembership,
  updateSingleAnswer,
  clearErrors,
  getRavenContact
} from '../actions/BookingForm';
import { fetchCountryStates } from '../actions/Config';
import { scrollOlaWidget } from '../actions/ScrollWidgetActions';
import { ResourceHelper } from '@wsb/ola-widget-components';
import { Fetching } from './Fetching';
import ConfigStore from '../stores/Config';
import ServiceInfo from './ServiceInfo';
import dataAids from '../constants/dataAids';
import { countries } from '../constants/countries';
import { formErrors } from '../constants/errors';
import renderModes from '../constants/renderModes';
import ServiceCostDetails from './ServiceCostDetails';
import ClientForm from './ClientForm';
import QuestionsForm from './QuestionsForm';
import PaymentSection from './PaymentSection';
import views from '../constants/views';
import { SQUARE, PAYPAL } from '../constants/paymentMethods';
import Disclaimer from './Disclaimer';
import { isSingleEvent } from '../utils/recurrenceType';
import { trackMetric } from '../../common/utils/trackMetric';
import { getServiceType } from '../utils/recurrenceType';
import {
  BOOKING_FORM_APPOINTMENT_IMPRESSION,
  BOOKING_FORM_SINGLE_EVENT_IMPRESSION,
  BOOKING_FORM_RECURRING_EVENT_IMPRESSION,
  BOOKING_FORM_BOOK_CTA_CLICK
} from '../constants/metrics';
import { trackPixel } from '../utils/FacebookPixel';

// TODO - Get based on current currency
const CURRENCY_SUBUNIT = 100;
const COST_TYPE_COST = 'cost';

const buttonStyles = {
  '@xs-only': {
    width: '100%'
  }
};
const styles = {
  inline: {
    display: 'inline'
  },
  formTitleBottomMargin: {
    marginBottom: 'medium'
  },
  formTitleNoMargin: {
    marginBottom: '0'
  },
  buttonPointer: {
    ...buttonStyles,
    cursor: 'pointer'
  },
  buttonNotAllowed: {
    ...buttonStyles,
    cursor: 'not-allowed'
  },
  error: {
    width: '100%',
    marginVertical: 'small'
  },
  resourceWrapper: {
    '@xs-only': {
      paddingBottom: 'xsmall'
    },
    '@sm': {
      paddingBottom: 'medium'
    }
  },
  deepLinkErrorSection: {
    marginTop: 'medium',
    paddingHorizontal: 'medium',
    paddingVertical: 'large',
    textAlign: 'center',
    backgroundColor: 'sectionOverlay'
  },
  serviceStyles: {
    name: {
      marginBottom: 'xsmall',
      fontWeight: 'bold',
      wordWrap: 'break-word',
      '@xs-only': {
        marginBottom: 'xxsmall',
        lineHeight: '1'
      }
    },
    date: {
      marginBottom: 'xsmall',
      '@xs-only': {
        marginBottom: 'xxsmall'
      }
    },
    durationAndCost: {
      display: 'block'
    },
    modifyLink: {
      cursor: 'pointer'
    },
    depositInfo: {
      marginTop: 'xsmall'
    }
  },
  sectionLabel: {
    fontWeight: 'bold'
  },
  sectionDisclaimer: {
    display: 'flex',
    alignItems: 'flex-start',
    marginTop: 'medium'
  },

  sectionDisclaimerText1: {
    marginLeft: 'xsmall',
    marginBottom: 'xxsmall'
  },

  sectionDisclaimerText2: {
    marginLeft: 'xsmall',
    marginBottom: 'medium'
  },
  separatorHorizontal: {
    '@xs-only': {
      marginBottom: 'medium'
    },
    '@sm': {
      marginBottom: 'large'
    }
  },
  separatorHorizontalXS: {
    '@sm': {
      display: 'none'
    }
  }
};

export default class BookingForm extends Component {
  static contextTypes = UX2.Base.contextTypes;

  constructor(props) {
    super(props);
    this.state = this.getStoreState();

    this.onStoreChange = this.onStoreChange.bind(this);
    this.updateClientWrapper = this.updateClientWrapper.bind(this);
    this.updateLocationWrapper = this.updateLocationWrapper.bind(this);
    this.onBookingSave = this.onBookingSave.bind(this);
  }

  getStoreState() {
    const state = BookingFormStore.getState();
    const { selectedAvailableTime, selectedResource } = AvailableTimeSelectionStore.getState();
    const { selectedService } = ServiceListStore.getState();
    const {
      env,
      countryStates,
      areCountryStatesLoaded,
      business_country: businessCountry,
      is_c2_phone_required: isC2PhoneRequired,
      is_membership_on: isMembershipOn,
      is_cost_details_always_shown: isCostDetailsAlwaysShown,
      questions,
      resources: { total: resourceCount } = {},
      membershipAccountsOn,
      widget_max_answer_length: maxAnswerLength,
      currency_format: { iso_code: currency },
      requiresMarketingConsent
    } = ConfigStore.getState();

    return {
      ...state,
      env,
      selectedAvailableTime,
      selectedResource,
      selectedService,
      countryStates,
      areCountryStatesLoaded,
      isC2PhoneRequired,
      isMembershipOn,
      isCostDetailsAlwaysShown,
      questions,
      resourceCount,
      businessCountry,
      membershipAccountsOn,
      maxAnswerLength,
      currency,
      requiresMarketingConsent,
      paymentOption: selectedService.payment_option
    };
  }

  componentDidMount() {
    const { isMembershipOn, membershipAccountsOn, requiresMarketingConsent, paymentOption } =
      this.state;

    BookingFormStore.addListener('change', this.onStoreChange);
    AvailableTimeSelectionStore.addListener('change', this.onStoreChange);
    ServiceListStore.addListener('change', this.onStoreChange);
    ConfigStore.addListener('change', this.onStoreChange);

    this.trackImpression();
    fetchCountryStates();

    const isGopayCartOn = ConfigStore.isGopayCartOn();

    if (paymentOption !== 'dont_charge_upfront' && !isGopayCartOn) {
      fetchActivePaymentMethods(this.getPaymentAmountInCents());
    }

    if (isMembershipOn && membershipAccountsOn && !isGopayCartOn) {
      getRavenContact().then(() => {
        const { isC2LoggedIn } = this.state;

        const membershipChecked = Boolean(!isC2LoggedIn && !requiresMarketingConsent);

        updateMembership(membershipChecked);
      });
    }

    scrollOlaWidget();
  }

  componentWillUnmount() {
    clearErrors();
    BookingFormStore.removeListener('change', this.onStoreChange);
    AvailableTimeSelectionStore.removeListener('change', this.onStoreChange);
    ServiceListStore.removeListener('change', this.onStoreChange);
    ConfigStore.removeListener('change', this.onStoreChange);
  }

  onStoreChange() {
    this.setState(this.getStoreState());
  }

  trackImpression() {
    const { selectedService } = this.state;
    const serviceType = getServiceType(selectedService.recurrence_type);

    const metricMap = {
      APPOINTMENT: BOOKING_FORM_APPOINTMENT_IMPRESSION,
      SINGLE_EVENT: BOOKING_FORM_SINGLE_EVENT_IMPRESSION,
      RECURRING_EVENT: BOOKING_FORM_RECURRING_EVENT_IMPRESSION
    };

    trackMetric(metricMap[serviceType]);
  }

  updateClientWrapper(field, value) {
    const { client } = this.state;
    const updatedClient = Object.assign({}, client, { [field]: value });

    updateClient(updatedClient);
  }

  updateLocationWrapper(field, value) {
    const { location } = this.state;
    const updatedLocation = Object.assign({}, location, { [field]: value });

    updateLocation(updatedLocation);
  }

  isPublishMode() {
    return this.props.renderMode === renderModes.PUBLISH;
  }

  convertToClassNames(style) {
    return this.getRenderedClassNames(UX2.Element.Element, { style });
  }

  getRenderedClassNames(ElementComponent, props = null) {
    const el = new ElementComponent(props, this.context);
    const instance = el.render ? el.render() : el;
    return typeof instance.type === 'function'
      ? this.getRenderedClassNames(instance.type, instance.props)
      : instance.props.className || '';
  }

  getNonFormErrorMessage(error) {
    const { type } = error;
    const {
      selectedService: { name: serviceName }
    } = this.state;
    const { staticContent } = this.props;

    const interpolate = translate(staticContent);

    switch (type) {
      case 'paypal_error':
        return staticContent.paymentPaypalError;
      case 'booking_create_payment':
        return staticContent.errorBookingCreatePay;
      case 'payment_init':
        return staticContent.paymentPaypalInit;
      case 'unavailable_time':
        return interpolate('errorUnavailableTime', { serviceName });
      case 'invalid_phone_number':
        return staticContent.errorPhoneValidation;
      case 'max_capacity':
        return interpolate('errorMaxCapacity', { serviceName });
      case 'book_lead_time':
        return interpolate('errorBookLeadTime', { serviceName });
      default:
        return staticContent.defaultError;
    }
  }

  onBookingSave(isSuccess) {
    if (!isSuccess) return;
    trackPixel('Schedule');
    this.props.navigate(views.BOOKING_CONFIRMATION);
  }

  getPaymentAmount() {
    const { selectedService, paymentOption } = this.state;
    const serviceCost = Number(selectedService.cost_with_tax || selectedService.cost);
    let total = serviceCost;
    const depositAmount = Number(selectedService.deposit_amount);

    if (
      depositAmount &&
      paymentOption === 'charge_deposit' &&
      Number(depositAmount) < serviceCost
    ) {
      total = Number(depositAmount);
    }

    return total;
  }

  getPaymentAmountInCents() {
    return Math.round(this.getPaymentAmount() * CURRENCY_SUBUNIT); // securepay talks in cents
  }

  getPaymentInfo() {
    const { currency } = this.state;
    return { amount: this.getPaymentAmount(), currency };
  }

  isFormValid() {
    const { client, location, selectedService, isC2PhoneRequired, businessCountry } = this.state;

    if (!ConfigStore.isGopayCartOn()) {
      if (!client.first_name) return false;
      if (!client.last_name) return false;
      if (!client.email) return false;
      if (isC2PhoneRequired && !client.phone_number) return false;
    }

    if (selectedService.location_type === 'customer') {
      if (!location.address_1) return false;
      if (!location.city) return false;
      if (businessCountry.postal_codes_required && !location.postal_code) return false;
    }

    return true;
  }

  isPaymentRequired() {
    const { isFetchingPaymentMethods, activePaymentMethods, paymentOption } = this.state;

    const activePaymentObjAvailable =
      !isFetchingPaymentMethods && Boolean(activePaymentMethods && activePaymentMethods.length);

    return (
      activePaymentObjAvailable &&
      (paymentOption === 'charge_upfront' || paymentOption === 'charge_deposit')
    );
  }

  isPartialPayment() {
    const { selectedService, paymentOption } = this.state;
    const hasDepositAmount = Boolean(Number(selectedService.deposit_amount));

    return this.isPaymentRequired() && hasDepositAmount && paymentOption === 'charge_deposit';
  }

  isPaypalOn() {
    const { activePaymentMethods } = this.state;

    return (
      this.isPaymentRequired() &&
      activePaymentMethods.some(paymentMethod => paymentMethod.key === PAYPAL)
    );
  }

  isSquareOn() {
    const { activePaymentMethods } = this.state;

    return (
      this.isPaymentRequired() &&
      activePaymentMethods.some(paymentMethod => paymentMethod.key === SQUARE)
    );
  }

  isSquareGiftCardApplicable() {
    const { activePaymentMethods } = this.state;
    return activePaymentMethods.some(paymentMethod => {
      const { key, gift_card_enabled: giftCardEnabled } = paymentMethod;
      return this.isPaymentRequired() && key === SQUARE && giftCardEnabled;
    });
  }

  isDisclaimerShown() {
    const { membershipAccountsOn, isMembershipOn, isC2LoggedIn, isRavenContactLoaded } = this.state;

    return (
      membershipAccountsOn &&
      isMembershipOn &&
      isRavenContactLoaded &&
      !isC2LoggedIn &&
      !ConfigStore.isGopayCartOn()
    );
  }

  getLoginRedirectUrl() {
    if (!this.isPublishMode()) return '#';

    const {
      routerLocation: { pathname, search }
    } = this.props;
    return [pathname, search].join('');
  }

  isCostDetailsShown() {
    const { isCostDetailsAlwaysShown, selectedService } = this.state;

    if (ConfigStore.isGopayCartOn() || selectedService.cost_type !== COST_TYPE_COST) return false;
    return (
      isCostDetailsAlwaysShown || (selectedService.tax_amount > 0 && selectedService.tax_category)
    );
  }

  isTaxRateShown() {
    const { businessCountry } = this.state;
    return businessCountry.iso === countries.GB.iso;
  }

  isSubmitDisabled() {
    // Note: to test GoPay Cart in editor, remove this.isPublishMode():
    if (ConfigStore.isGopayCartOn() && this.isPublishMode()) {
      return !this.isFormValid();
    }

    return !this.isPublishMode();
  }

  onClickConfirm = () => {
    trackMetric(BOOKING_FORM_BOOK_CTA_CLICK);
    return saveUnpaidBooking().then(this.onBookingSave);
  };

  isClientFormShown() {
    const { areCountryStatesLoaded, selectedService } = this.state;

    return ConfigStore.isGopayCartOn()
      ? selectedService.location_type === 'customer' && areCountryStatesLoaded
      : areCountryStatesLoaded;
  }

  get confirmBookingText() {
    const { staticContent } = this.props;
    if (ConfigStore.isGopayCartOn()) {
      return staticContent.book;
    }
    return staticContent.bookAppointment;
  }

  /* eslint-disable complexity */
  render() {
    const {
      activePaymentMethods,
      answers,
      businessCountry,
      client,
      countryStates,
      deepLinkError,
      membership,
      marketing,
      env,
      error,
      isC2PhoneRequired,
      isC2LoggedIn,
      isFetching,
      isFetchingRavenContact,
      isMembershipOn,
      location,
      maxAnswerLength,
      paypalError,
      questions,
      resourceCount,
      selectedAvailableTime,
      selectedResource,
      selectedService,
      membershipAccountsOn,
      requiresMarketingConsent
    } = this.state;

    const { section, staticContent } = this.props;

    const { Block, Label, Text, HR, Error } = UX2.Element;
    const {
      Grid,
      Grid: { Cell }
    } = UX2.Component;

    const isSingleEventService = isSingleEvent(selectedService.recurrence_type);

    const disclaimerAbovePayment =
      this.isDisclaimerShown() &&
      this.isPaymentRequired() &&
      this.isPaypalOn() &&
      this.isSquareOn();
    const disclaimerBelowPayment = this.isDisclaimerShown() && !disclaimerAbovePayment;

    const hasNonFormError = error && !formErrors.includes(error.type);

    // TODO: Nest input fields in a FORM tag with an action so that form can be submitted with blue GO button on iphone
    return (
      <Grid size={ 6 }>
        <Cell xs={ 6 } md={ 4 } pushMd={ 1 }>
          <Fetching fetching={ isFetching || isFetchingRavenContact } />
          <Block>
            <HR style={ styles.separatorHorizontalXS } />
            <ServiceInfo
              data-aid={ dataAids.BOOKING_FORM_SERVICE_INFO_RENDERED }
              service={ selectedService }
              serviceStyles={ styles.serviceStyles }
              depositShown={ this.isPartialPayment() }
              selectedAvailableTime={
                isSingleEventService ? selectedService.start_time : selectedAvailableTime
              }
              staticContent={ staticContent }
              modifyLink={
                !isSingleEventService && (() => this.props.navigate(views.AVAILABLE_TIME_SELECTION))
              }
              isDescriptionShown={ isSingleEventService }
              isDescriptionTrimmable
            />
            { selectedResource && resourceCount > 1 && (
              <Block data-aid={ dataAids.BOOKING_FORM_RESOURCE } style={ styles.resourceWrapper }>
                <Label style={ styles.sectionLabel }>{ staticContent.staffMember }</Label>
                <Text>
                  { ResourceHelper.getName(selectedResource, {
                    anyResourceName: staticContent.anyStaff,
                    ownerResourceName: staticContent.businessOwner
                  }) }
                </Text>
              </Block>
            ) }
            { this.isCostDetailsShown() && (
              <ServiceCostDetails
                service={ selectedService }
                staticContent={ staticContent }
                isTaxRateShown={ this.isTaxRateShown() }
              />
            ) }
            { !ConfigStore.isGopayCartOn() && <HR style={ styles.separatorHorizontal } /> }
          </Block>
          { deepLinkError ? (
            <Block
              section={ section === 'default' ? 'alt' : 'default' }
              style={ styles.deepLinkErrorSection }
            >
              <Text data-aid={ dataAids.BOOKING_FORM_DEEP_LINK_ERROR } featured>
                { staticContent.errorNoLongerAvailable }
              </Text>
            </Block>
          ) : (
            <Block>
              { this.isClientFormShown() && (
                <ClientForm
                  titleStyles={ styles.formTitleBottomMargin }
                  error={ error }
                  onUpdateClient={ this.updateClientWrapper }
                  onUpdateLocation={ this.updateLocationWrapper }
                  client={ client }
                  location={ location }
                  service={ selectedService }
                  staticContent={ staticContent }
                  businessCountry={ businessCountry }
                  countryStates={ countryStates }
                  isC2PhoneRequired={ isC2PhoneRequired }
                  isC2LoggedIn={ isC2LoggedIn }
                  isMembershipOn={ isMembershipOn }
                  loginRedirectUrl={ this.getLoginRedirectUrl() }
                  membershipAccountsOn={ membershipAccountsOn }
                  marketing={ marketing }
                  requiresConsent={ this.isDisclaimerShown() && requiresMarketingConsent }
                />
              ) }

              { !ConfigStore.isGopayCartOn() && questions && questions.length > 0 && (
                <Fragment>
                  <HR style={ styles.separatorHorizontal } />

                  <QuestionsForm
                    answers={ answers }
                    error={ error }
                    maxAnswerLength={ maxAnswerLength }
                    onAnswerQuestions={ updateSingleAnswer }
                    questions={ questions }
                    staticContent={ staticContent }
                    titleStyles={ styles.formTitleBottomMargin }
                  />
                </Fragment>
              ) }

              { disclaimerAbovePayment && (
                <Fragment>
                  <HR style={ styles.separatorHorizontal } />
                  <Disclaimer
                    styles={ styles }
                    disclaimer={ membership }
                    staticContent={ staticContent }
                  />
                </Fragment>
              ) }

              { this.isPaymentRequired() && !ConfigStore.isGopayCartOn() && (
                <Fragment>
                  <HR style={ styles.separatorHorizontal } />
                  <PaymentSection
                    titleStyles={ styles.formTitleNoMargin }
                    paymentMethods={ activePaymentMethods }
                    isPublishMode={ this.isPublishMode() }
                    service={ selectedService }
                    client={ client }
                    paymentInfo={ this.getPaymentInfo() }
                    staticContent={ staticContent }
                    isPartialPayment={ this.isPartialPayment() }
                    isPaypalOn={ this.isPaypalOn() }
                    isSquareOn={ this.isSquareOn() }
                    isSquareGiftCardApplicable={ this.isSquareGiftCardApplicable() }
                    env={ env }
                    submitText={ staticContent.bookAppointment }
                    isFormValid={ this.isFormValid() }
                    onBookingSave={ this.onBookingSave }
                    paypalErrors={ paypalError ? [this.getNonFormErrorMessage(paypalError)] : [] }
                  />
                </Fragment>
              ) }

              { hasNonFormError && (
                <Error data-aid={ dataAids.BOOKING_FORM_INPUT_ERROR } style={ styles.error }>
                  { this.getNonFormErrorMessage(error) }
                </Error>
              ) }

              { (!this.isPaymentRequired() || ConfigStore.isGopayCartOn()) && (
                <ConfirmBookingButton
                  isCartOn={ ConfigStore.isGopayCartOn() }
                  data-aid={ dataAids.BOOKING_FORM_BOOK_APPOINTMENT_BUTTON }
                  onClick={ this.onClickConfirm }
                  disabled={ this.isSubmitDisabled() }
                  navigate={ this.props.navigate }
                  style={ this.isSubmitDisabled() ? styles.buttonNotAllowed : styles.buttonPointer }
                >
                  { this.confirmBookingText }
                </ConfirmBookingButton>
              ) }

              { Boolean(disclaimerBelowPayment) && (
                <Disclaimer
                  styles={ styles }
                  disclaimer={ membership }
                  staticContent={ staticContent }
                  requiresConsent={ requiresMarketingConsent }
                />
              ) }
            </Block>
          ) }
        </Cell>
      </Grid>
    );
  }
  /* eslint-enable complexity */
}

BookingForm.propTypes = {
  renderMode: PropTypes.string,
  section: PropTypes.string,
  routerLocation: PropTypes.shape({
    href: PropTypes.string.isRequired,
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired
  }).isRequired,
  navigate: PropTypes.func,
  staticContent: PropTypes.object.isRequired
};

export const ConfirmBookingButton = ({ onClick, isCartOn, navigate, disabled, ...props }) => {
  const cart = !disabled && useCart({ onAddToCart: () => navigate(views.SERVICE_LIST) }).cart;

  const { Button } = UX2.Element;

  if (!cart && isCartOn && !disabled) {
    return <Fetching fetching />;
  }

  const handleClick = isCartOn && cart ? cart.addCurrentService : onClick;

  return <Button.Primary { ...props } onClick={ handleClick } disabled={ disabled } />;
};

ConfirmBookingButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  isCartOn: PropTypes.bool.isRequired,
  navigate: PropTypes.func,
  disabled: PropTypes.bool
};
